// AIMS PWA - sync engine / reconcile loop (AIMS-Offline-Inspection.md §4).
//
// The single reconcile loop, run both manually ("Sync now") and automatically
// (app launch + `online` event). Idempotent and safe to spam - pushes dedupe on
// clientInspectionId server-side, and concurrent triggers coalesce onto one
// in-flight run.
//
//   1. Refresh JWT from bToken (validates the durable offline credential).
//   2. PUSH the outbox → /inspection/results/batch; mark each item syncedAt as
//      its per-item ack lands (synced or duplicate). Errors stay pending.
//   3. (PUSH images - Phase 5, not yet.)
//   4. PULL /offline-sync per checked-out visit → reconcile POOL MEMBERSHIP:
//      upsert server assets (authoritative status), cache any new audit-type
//      templates, drop removed assets UNLESS they still have pending work, and
//      re-stamp still-pending items so "done by me" survives a partial push.
//   5. Update syncMeta (last sync, counts, visit status).

import db from './db';
import { refreshJwt, pushResultsBatch, offlineSync, fetchChecklistTemplate } from './offlineApi';
import { releaseVisitOffline, pendingCountForVisit } from './takeOffline';

let _inFlight = null;

const norm = (s) => (s == null ? '' : String(s));
const STATUS_TITLE = { PASS: 'Pass', FAIL: 'Fail', NTI: 'NTI' };

// Is there anything to sync (pending pushes OR any checked-out visit to refresh)?
export async function hasPendingWork() {
  try {
    const pending = await db.outbox.filter((o) => !o.syncedAt).count();
    if (pending > 0) return true;
    return (await db.syncMeta.count()) > 0;
  } catch (_) {
    return false;
  }
}

export async function getPendingCount() {
  try {
    return await db.outbox.filter((o) => !o.syncedAt).count();
  } catch (_) {
    return 0;
  }
}

// Run the reconcile loop. Returns a summary; concurrent calls share one run.
export function runSync(opts = {}) {
  if (_inFlight) return _inFlight;
  _inFlight = _run(opts).finally(() => { _inFlight = null; });
  return _inFlight;
}

async function _run({ onProgress } = {}) {
  const report = (phase, done, total) => { if (onProgress) onProgress({ phase, done, total }); };
  const summary = { pushed: 0, duplicates: 0, failed: 0, pulledVisits: 0, clearedVisits: [], needsReauth: false, error: null };

  // 1) Refresh JWT - also our reachability + credential check.
  report('auth', 0, 1);
  try {
    await refreshJwt();
  } catch (e) {
    if (e && e.status === 401) { summary.needsReauth = true; return summary; } // bToken killed - preserve queue
    summary.error = 'offline'; // network unreachable
    return summary;
  }
  report('auth', 1, 1);

  // 2) Push the outbox.
  const pending = await db.outbox.filter((o) => !o.syncedAt).toArray();
  report('push', 0, pending.length);
  if (pending.length) {
    const items = pending.map((o) => ({
      clientInspectionId: o.clientInspectionId,
      siteVisitId: o.siteVisitId != null ? o.siteVisitId : o.visitId,
      catNo: o.catNo, customer: o.customer, auditType: o.auditType,
      inspectedBy: o.inspectedBy, inspectionDate: o.inspectionDate,
      reinspectionDue: o.reinspectionDue, checklist: o.checklist,
      achievedScore: o.achievedScore, overallResult: o.overallResult,
      notes: o.notes, firstTimeInspected: o.firstTimeInspected,
    }));
    try {
      const { results } = await pushResultsBatch(items);
      const now = Date.now();
      await db.transaction('rw', db.outbox, async () => {
        for (const r of (results || [])) {
          if (r.status === 'synced' || r.status === 'duplicate') {
            await db.outbox.update(r.clientInspectionId, { syncedAt: now, serverId: r.id || null });
            if (r.status === 'synced') summary.pushed++; else summary.duplicates++;
          } else {
            summary.failed++; // leave pending; next sync retries (idempotent)
          }
        }
      });
    } catch (e) {
      if (e && e.status === 401) { summary.needsReauth = true; return summary; }
      summary.error = 'push'; // network dropped mid-push; items stay pending
    }
    report('push', pending.length, pending.length);
  }

  // 3) Images - Phase 5.

  // 4) Pull + reconcile each checked-out visit.
  const metas = await db.syncMeta.toArray();
  report('pull', 0, metas.length);
  for (let i = 0; i < metas.length; i++) {
    const vid = metas[i].visitId;
    try {
      // eslint-disable-next-line no-await-in-loop
      const data = await offlineSync(vid);

      // The server says our checkout was cleared (HQ force-clear or a teammate
      // released the visit lease). The push in step 2 already ran, so if
      // nothing is left pending we drop the local copy (localOnly: the server
      // lease is already released) and surface a notice via the summary. If
      // unsynced work remains (the push failed this run), keep everything and
      // reconcile as normal; the next successful sync will land here again.
      if (data.checkout && data.checkout.cleared) {
        // eslint-disable-next-line no-await-in-loop
        const pendingLeft = await pendingCountForVisit(vid);
        if (pendingLeft === 0) {
          // eslint-disable-next-line no-await-in-loop
          await releaseVisitOffline(vid, { localOnly: true });
          summary.clearedVisits.push({ visitId: vid, clearedBy: data.checkout.clearedBy || null });
          summary.pulledVisits++;
          report('pull', i + 1, metas.length);
          continue;
        }
      }

      // eslint-disable-next-line no-await-in-loop
      await reconcileVisit(vid, data);
      summary.pulledVisits++;
    } catch (e) {
      // 404 = the visit was deleted (or never existed) server-side, so there
      // is nothing left to reconcile against. Same treatment as a cleared
      // checkout: once nothing is pending (the push in step 2 already ran,
      // and the batch endpoint accepts inspections regardless of the visit),
      // drop the local copy and tell the user. Any other error: leave this
      // visit's cache untouched and retry next sync.
      if (e && e.status === 404) {
        // eslint-disable-next-line no-await-in-loop
        const pendingLeft = await pendingCountForVisit(vid);
        if (pendingLeft === 0) {
          // eslint-disable-next-line no-await-in-loop
          await releaseVisitOffline(vid, { localOnly: true });
          summary.clearedVisits.push({ visitId: vid, clearedBy: null, visitDeleted: true });
        }
      }
    }
    report('pull', i + 1, metas.length);
  }

  return summary;
}

// Reconcile one visit's cached pool against the authoritative server delta.
async function reconcileVisit(visitId, data) {
  const serverAssets = Array.isArray(data.assets) ? data.assets : [];
  const auditTypes = Array.isArray(data.auditTypesNeeded) ? data.auditTypesNeeded : [];

  // Fetch any newly-needed templates BEFORE the transaction (no network in tx).
  for (const at of auditTypes) {
    // eslint-disable-next-line no-await-in-loop
    const existing = await db.checklistTemplates.get(at);
    if (!existing) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const items = await fetchChecklistTemplate(at);
        // eslint-disable-next-line no-await-in-loop
        await db.checklistTemplates.put({ auditType: at, items, cachedAt: Date.now() });
      } catch (_) { /* template fetch failed - asset still lists, checklist retried next sync */ }
    }
  }

  const serverCatNos = new Set(serverAssets.map((a) => norm(a.catNo)));

  await db.transaction('rw', db.assets, db.outbox, db.syncMeta, async () => {
    // Upsert server assets - server is authoritative for status/membership.
    for (const a of serverAssets) {
      await db.assets.put({ ...a, visitId, catNo: norm(a.catNo), cachedAt: Date.now() });
    }

    // Removals + re-stamp still-pending optimistic status in one pass.
    const cached = await db.assets.where('visitId').equals(visitId).toArray();
    for (const c of cached) {
      const catNo = norm(c.catNo);
      const pendingItems = await db.outbox
        .filter((o) => o.visitId === visitId && norm(o.catNo) === catNo && !o.syncedAt)
        .toArray();
      const hasPending = pendingItems.length > 0;

      if (!serverCatNos.has(catNo)) {
        // Server dropped it from the visit. Keep only if we still owe an upload.
        if (!hasPending) await db.assets.delete([visitId, catNo]);
        continue;
      }
      if (hasPending) {
        // Push hasn't landed yet - keep the local "done by me" stamp visible so
        // the tech doesn't see it revert to Outstanding before the retry syncs.
        const last = pendingItems[pendingItems.length - 1];
        const titled = STATUS_TITLE[String(last.overallResult || '').toUpperCase()] || last.overallResult;
        if (titled) {
          await db.assets.update([visitId, catNo], {
            inspectionStatus: titled,
            completedThisVisit: true,
            inspectedBy: last.inspectedBy || c.inspectedBy || null,
          });
        }
      }
    }

    await db.syncMeta.update(visitId, {
      lastSyncAt: Date.now(),
      assetCount: serverAssets.length,
      status: data.visitStatus,
    });
  });
}
